import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Check } from "lucide-react";

/**
 * Generic collapsible result section — mirrors the dashboard phase cards.
 *
 * Usage:
 *   <ResultSection
 *     title="Adjacent roles"
 *     eyebrow="Skills 90% transferable"
 *     accentGradient="linear-gradient(135deg,#3B82F6 0%,#1E40AF 100%)"
 *     count={5}
 *     defaultOpen
 *     group="role-sections"
 *   >
 *     ...content...
 *   </ResultSection>
 *
 * Props:
 *   - title         Required. Section heading.
 *   - eyebrow       Optional short subtitle above the title.
 *   - accentGradient Optional CSS gradient string for the header bar.
 *   - count         Optional badge number rendered next to the title.
 *   - done          Optional bool — shows a green ✓ pill.
 *   - defaultOpen   Defaults to false. Auto-expand on mount.
 *   - testid        Optional data-testid override.
 *   - group         Optional group key — when an "Expand all / Collapse all"
 *                   toolbar dispatches a `resmo:result-section:set-all`
 *                   CustomEvent with matching `detail.group`, every section
 *                   in the group will sync to `detail.open`.
 */
export default function ResultSection({
  title,
  eyebrow,
  accentGradient,
  count,
  done = false,
  defaultOpen = false,
  testid,
  group,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const ti = testid || `section-${(title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  // Listen for a group-level set-all event so a parent toolbar can
  // expand/collapse every section at once without prop-drilling state.
  useEffect(() => {
    if (!group) return undefined;
    const handler = (e) => {
      if (!e?.detail || e.detail.group !== group) return;
      setOpen(!!e.detail.open);
    };
    window.addEventListener("resmo:result-section:set-all", handler);
    return () => window.removeEventListener("resmo:result-section:set-all", handler);
  }, [group]);

  // A tab strip (ReportTabStrip) can open one specific section by testid.
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.testid === ti) setOpen(true);
    };
    window.addEventListener("resmo:result-section:open", handler);
    return () => window.removeEventListener("resmo:result-section:open", handler);
  }, [ti]);

  return (
    <div
      className="rm-card-static overflow-hidden"
      style={{
        background: "white",
        borderColor: "#0F1B2D",
        boxShadow: "4px 4px 0 0 #0F1B2D",
        padding: 0,
      }}
      data-testid={ti}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left flex items-center gap-3 px-4 py-3 transition-colors"
        style={{
          background: accentGradient || "white",
          color: accentGradient ? "white" : "#0F1B2D",
          borderBottom: open ? "2px solid #0F1B2D" : "none",
        }}
        aria-expanded={open}
        data-testid={`${ti}-toggle`}
      >
        <div className="flex-1 min-w-0">
          {eyebrow && (
            <div
              className="font-mono-rm text-[10px] uppercase tracking-widest mb-0.5"
              style={{ color: accentGradient ? "rgba(255,255,255,0.85)" : "#64748B", fontWeight: 600 }}
            >
              · {eyebrow}
            </div>
          )}
          <div
            className="font-display uppercase text-[15px] leading-tight flex items-center gap-2 truncate"
            style={{ letterSpacing: "-0.01em", fontWeight: 700 }}
          >
            <span className="truncate">{title}</span>
            {typeof count === "number" && (
              <span
                className="font-mono-rm text-[10px] px-1.5 py-0.5"
                style={{
                  background: accentGradient ? "rgba(255,255,255,0.18)" : "#FFC145",
                  color: accentGradient ? "white" : "#0F1B2D",
                  border: `1px solid ${accentGradient ? "rgba(255,255,255,0.4)" : "#0F1B2D"}`,
                  borderRadius: 4,
                  fontWeight: 700,
                }}
              >
                {count}
              </span>
            )}
            {done && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5"
                style={{
                  background: accentGradient ? "rgba(16, 185, 129, 0.25)" : "#ECFDF5",
                  color: accentGradient ? "white" : "#047857",
                  border: `1px solid ${accentGradient ? "rgba(167, 243, 208, 0.6)" : "#A7F3D0"}`,
                  borderRadius: 4,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                }}
              >
                <Check size={10} strokeWidth={3} /> Done
              </span>
            )}
          </div>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      <div
        className="overflow-hidden"
        style={{
          display: "grid",
          gridTemplateRows: open ? "1fr" : "0fr",
          transition: "grid-template-rows 200ms ease-out",
        }}
        aria-hidden={!open}
        data-testid={`${ti}-body`}
      >
        <div style={{ minHeight: 0, overflow: "hidden" }}>
          <div className="p-4 md:p-5">{children}</div>
        </div>
      </div>
    </div>
  );
}
